import React from 'react';
import { CollapsibleSection } from '../collapsible-section/CollapsibleSection';
import { t } from '../../services/locale';
import type { ProfileAccountBranch, ProfileAddress, ProfileIdentity } from './profileAccount.types';
import { hasUserAddress, outstandingProfileInfo, showTaxIdOnProfile } from './profileIdentityRules';

export type ProfileSummaryUser = ProfileIdentity & {
  name?: string | null;
  membership?: {
    membership_name?: string | null;
    membership_plan_name?: string | null;
  } | null;
};

export type ProfileSummaryData = {
  profile?: ProfileSummaryUser | null;
  branch?: ProfileAccountBranch | null;
  creditsAvailable?: number | null;
  imageVersion?: number | string;
  isAddressExpanded?: boolean;
  isGuardAccount?: boolean;
  linkedAccountCount?: number;
  onLogOut?: () => void;
  onEdit?: () => void;
  onToggleAddress?: () => void;
  onOpenFamilyAccounts?: () => void;
};

type Props = Readonly<{
  data?: ProfileSummaryData;
}>;

function decodeAmpersand(value: string): string {
  return value.replace(/&amp;/g, '&');
}

function onActivateKey(event: React.KeyboardEvent, action?: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  action?.();
}

function LogoutIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M7.5 1v7h1V1z" />
      <path d="M3 8.812a5 5 0 0 1 2.578-4.375l-.485-.874A6 6 0 1 0 11 3.616l-.501.865A5 5 0 1 1 3 8.812" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14.69 3.1l6.21 6.2-1.42 1.42-6.2-6.21 1.41-1.41zm-2.83 2.83l6.2 6.2L7.76 22H1v-6.76l10.86-10.3zm-8.4 13.53h2.83l8.3-8.3-2.83-2.83-8.3 8.3V19.46z" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
        fill="#1793e5"
      />
    </svg>
  );
}

function pictureStyle(profile: ProfileSummaryUser, imageVersion?: number | string): React.CSSProperties | undefined {
  if (!profile.image_url) {
    return undefined;
  }
  const version = imageVersion ?? '';
  return { backgroundImage: `url(${profile.image_url}?ver=${version})` };
}

function addressLine(address: ProfileAddress | null | undefined): string {
  if (!address) {
    return '';
  }
  return [address.street, address.city, address.state, address.country, address.postal_code]
    .filter((part) => part)
    .join(' ');
}

function LogoutControl({ onLogOut }: Readonly<{ onLogOut?: () => void }>) {
  return (
    <div
      className="log-out-svg"
      data-testid="logout-profile"
      role="button"
      tabIndex={0}
      aria-label={t('WEB_LOGOUT')}
      onClick={() => onLogOut?.()}
      onKeyDown={(event) => onActivateKey(event, onLogOut)}
    >
      <LogoutIcon />
      <span className="log-out-text">{t('WEB_LOGOUT')}</span>
    </div>
  );
}

function EditControl({ onEdit }: Readonly<{ onEdit?: () => void }>) {
  return (
    <div
      className="edit-profile-svg"
      role="button"
      tabIndex={0}
      aria-label="Edit"
      onClick={() => onEdit?.()}
      onKeyDown={(event) => onActivateKey(event, onEdit)}
    >
      <EditIcon />
    </div>
  );
}

function IdentityRow({
  label,
  value,
  id,
  className = 'text-row',
}: Readonly<{
  label: string;
  value?: string | null;
  id?: string;
  className?: string;
}>) {
  if (!value) {
    return null;
  }
  return (
    <div className={className}>
      {label}:
      <span id={id} className="blue"> {value}</span>
    </div>
  );
}

function FamilyTeaser({
  count,
  onOpenFamilyAccounts,
}: Readonly<{ count: number; onOpenFamilyAccounts?: () => void }>) {
  return (
    <div>
      <div className="list-label">
        <span>{t('WEB_FAMILY_ACCOUNTS')}</span>
        <a
          className="add-card"
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onOpenFamilyAccounts?.();
          }}
        >
          <p>+</p>
        </a>
      </div>
      <ul className="list cards">
        <li className="noBorder">
          <div
            className="list-text-left"
            role="button"
            tabIndex={0}
            onClick={() => onOpenFamilyAccounts?.()}
            onKeyDown={(event) => onActivateKey(event, onOpenFamilyAccounts)}
          >
            {count === 0
              ? t('WEB_CREATE_OR_ADD_AN_ACCOUNT')
              : `(${count}) ${t('WEB_ACCOUNTS')}`}
          </div>
        </li>
      </ul>
    </div>
  );
}

function OutstandingWarning({ show }: Readonly<{ show: boolean }>) {
  if (!show) {
    return null;
  }
  return (
    <div>
      <div className="warning">
        <WarningIcon />
        <p className="warning__message">{t('WEB_OUTSTANDING_PROFILE_DETAILS')}</p>
      </div>
    </div>
  );
}

function TaxIdRow({
  branch,
  taxId,
}: Readonly<{
  branch?: ProfileAccountBranch | null;
  taxId?: string | null;
}>) {
  if (!showTaxIdOnProfile(branch) || !taxId) {
    return null;
  }
  return (
    <IdentityRow
      label={t('WEB_MEMBER_TAX_ID_NUMBER')}
      value={taxId}
    />
  );
}

function AddressBlock({
  profile,
  isExpanded,
  onToggleAddress,
}: Readonly<{
  profile: ProfileSummaryUser;
  isExpanded?: boolean;
  onToggleAddress?: () => void;
}>) {
  if (!hasUserAddress(profile)) {
    return null;
  }
  return (
    <CollapsibleSection
      data={{
        id: 'collapsibleAdressField',
        label: t('WEB_ADDRESS'),
        expanded: Boolean(isExpanded),
        onToggle: () => onToggleAddress?.(),
      }}
    >
      <p>{addressLine(profile.address)}</p>
    </CollapsibleSection>
  );
}

function IdentityDetails({
  profile,
  branch,
  isAddressExpanded,
  onToggleAddress,
}: Readonly<{
  profile: ProfileSummaryUser;
  branch?: ProfileAccountBranch | null;
  isAddressExpanded?: boolean;
  onToggleAddress?: () => void;
}>) {
  return (
    <>
      <div className="text-row">
        {t('WEB_EMAIL')}:
        <span className="blue"> {profile.email}</span>
      </div>
      <IdentityRow label={t('WEB_PHONE')} value={profile.phone} />
      <IdentityRow
        label={decodeAmpersand(t('WEB_EMERGENCY_CONTACT'))}
        value={profile.emergency_contact}
      />
      <IdentityRow
        label={t('WEB_MEMBERSHIP')}
        value={profile.membership?.membership_name}
        id="profile-membership-name"
      />
      <IdentityRow label={t('WEB_MEMBERSHIP_PLAN')} value={profile.membership?.membership_plan_name} />
      <TaxIdRow branch={branch} taxId={profile.metadata?.fiscal?.tax_id} />
      <AddressBlock
        profile={profile}
        isExpanded={isAddressExpanded}
        onToggleAddress={onToggleAddress}
      />
      <OutstandingWarning show={outstandingProfileInfo(profile, branch)} />
    </>
  );
}

export function ProfileSummary({ data }: Props) {
  if (!data?.profile) {
    return null;
  }

  const profile = data.profile;
  const showCredits = data.creditsAvailable !== null && data.creditsAvailable !== undefined;

  return (
    <>
      <LogoutControl onLogOut={data.onLogOut} />
      <div className="picture">
        <div className="picture-inner" style={pictureStyle(profile, data.imageVersion)} />
      </div>
      <div className="text">
        <div className="text-row">
          <span className="name">{profile.name}</span>
        </div>
        <div className="edit-profile">
          <div className="credits-text">
            {showCredits ? (
              <span id="profile-credits" className="credits text-row show-overflow lower">
                {data.creditsAvailable} {t('WEB_CREDITS')}
              </span>
            ) : null}
          </div>
          <div>
            <EditControl onEdit={data.onEdit} />
          </div>
        </div>
        <IdentityDetails
          profile={profile}
          branch={data.branch}
          isAddressExpanded={data.isAddressExpanded}
          onToggleAddress={data.onToggleAddress}
        />
      </div>
      {data.isGuardAccount ? (
        <FamilyTeaser count={data.linkedAccountCount ?? 0} onOpenFamilyAccounts={data.onOpenFamilyAccounts} />
      ) : null}
    </>
  );
}
